import EmblaCarousel, { type EmblaCarouselType } from 'embla-carousel'

/**
 * Carrosséis de exemplos (foto de perfil e stories).
 *
 * Abaixo de 768px vira carrossel de 1 item por vez, sempre.
 * A partir de 768px o grid de 3 colunas continua valendo enquanto houver
 * até 3 exemplos; com 4 ou mais o bloco vira carrossel de 3 por vez, para
 * os itens não quebrarem para a linha de baixo.
 *
 * O CSS só assume o modo carrossel quando `data-slider-ready` /
 * `data-slider-mode` são aplicados aqui — sem JS, o grid original continua
 * funcionando.
 */

const MOBILE = '(max-width: 767px)'
const AUTOPLAY_PADRAO = 5000
/** quantos itens cabem por vez no desktop (mesmo número de colunas do grid) */
const POR_VEZ_DESKTOP = 3

type Modo = 'mobile' | 'desktop'

const SETAS: Record<'prev' | 'next', string> = {
  prev: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 4 7 12l8 8" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  next: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9 4 8 8-8 8" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
}

function buildDots(embla: EmblaCarouselType, dotsNode: HTMLElement): void {
  const render = (): void => {
    dotsNode.innerHTML = embla
      .scrollSnapList()
      .map(
        (_, i) =>
          `<button type="button" role="tab" aria-selected="false" aria-label="Exemplo ${i + 1}"></button>`,
      )
      .join('')

    dotsNode.querySelectorAll('button').forEach((dot, i) => {
      dot.addEventListener('click', () => embla.scrollTo(i))
    })
  }

  const sync = (): void => {
    const selected = embla.selectedScrollSnap()
    dotsNode.querySelectorAll('button').forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === selected))
    })
  }

  embla.on('init', render).on('reInit', render)
  embla.on('init', sync).on('reInit', sync).on('select', sync)
}

/** Setas só existem no desktop; no mobile o gesto de arrastar já resolve. */
function buildArrows(embla: EmblaCarouselType, root: HTMLElement): HTMLElement[] {
  const botoes = (['prev', 'next'] as const).map((lado) => {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = `vd-slider__seta vd-slider__seta--${lado}`
    btn.setAttribute('aria-label', lado === 'prev' ? 'Exemplo anterior' : 'Próximo exemplo')
    btn.innerHTML = SETAS[lado]
    btn.addEventListener('click', () =>
      lado === 'prev' ? embla.scrollPrev() : embla.scrollNext(),
    )
    root.appendChild(btn)
    return btn
  })

  // com poucos itens o embla desativa o loop sozinho; aí as pontas travam
  const sync = (): void => {
    botoes[0].disabled = !embla.canScrollPrev()
    botoes[1].disabled = !embla.canScrollNext()
  }
  embla.on('init', sync).on('reInit', sync).on('select', sync)

  return botoes
}

function setupSlider(root: HTMLElement): void {
  const viewport = root.querySelector<HTMLElement>('[data-slider-viewport]')
  const dots = root.querySelector<HTMLElement>('[data-slider-dots]')
  if (!viewport) return

  const total = viewport.querySelectorAll('li').length
  if (total < 2) return

  const mq = window.matchMedia(MOBILE)
  let embla: EmblaCarouselType | null = null
  let modoAtual: Modo | null = null
  let setas: HTMLElement[] = []

  const delay = Number(root.dataset.sliderAutoplay ?? AUTOPLAY_PADRAO)
  const temAutoplay = delay > 0
  // quem pediu menos movimento no sistema continua vendo os exemplos passarem,
  // só que sem a animação de deslize
  const semAnimacao = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  let timer = 0
  let pausado = false

  /** um único intervalo cuida do ciclo; pausar é só levantar a flag, para o
      autoplay nunca ficar preso caso um evento de retomada não chegue */
  const passar = (): void => {
    if (!embla || pausado || document.hidden) return
    if (embla.canScrollNext()) embla.scrollNext(semAnimacao)
    else embla.scrollTo(0, semAnimacao) // sem loop (poucos itens), volta ao início
  }

  const ligarAutoplay = (): void => {
    if (!temAutoplay || timer) return
    timer = window.setInterval(passar, delay)
  }

  const desligarAutoplay = (): void => {
    window.clearInterval(timer)
    timer = 0
    pausado = false
  }

  /** zera a contagem para não pular logo depois de a pessoa escolher um exemplo */
  const reiniciarContagem = (): void => {
    if (!timer) return
    window.clearInterval(timer)
    timer = window.setInterval(passar, delay)
  }

  /** mobile sempre; desktop só quando não cabe tudo nas 3 colunas */
  const modoAlvo = (): Modo | null => {
    if (mq.matches) return 'mobile'
    return total > POR_VEZ_DESKTOP ? 'desktop' : null
  }

  const start = (modo: Modo): void => {
    if (embla) return
    modoAtual = modo
    root.setAttribute('data-slider-ready', '')
    root.setAttribute('data-slider-mode', modo)

    embla = EmblaCarousel(viewport, {
      loop: true,
      align: modo === 'mobile' ? 'center' : 'start',
      slidesToScroll: 1,
    })

    if (dots) buildDots(embla, dots)
    if (modo === 'desktop') setas = buildArrows(embla, root)

    if (!temAutoplay) return
    // arrastar, clicar nas setas ou nas bolinhas reinicia a contagem, para o
    // carrossel não pular logo depois de a pessoa escolher um exemplo
    embla
      .on('pointerDown', () => {
        pausado = true
      })
      .on('pointerUp', () => {
        pausado = false
        reiniciarContagem()
      })
    root.addEventListener('click', reiniciarContagem)
    ligarAutoplay()
  }

  const stop = (): void => {
    if (!embla) return
    desligarAutoplay()
    root.removeEventListener('click', reiniciarContagem)
    embla.destroy()
    embla = null
    modoAtual = null
    for (const seta of setas) seta.remove()
    setas = []
    if (dots) dots.innerHTML = ''
    root.removeAttribute('data-slider-ready')
    root.removeAttribute('data-slider-mode')
  }

  const apply = (): void => {
    const alvo = modoAlvo()
    if (alvo === modoAtual) return
    stop()
    if (alvo) start(alvo)
  }

  apply()
  mq.addEventListener('change', apply)
}

function initSliders(): void {
  document
    .querySelectorAll<HTMLElement>('[data-slider]')
    .forEach((root) => setupSlider(root))
}

document.addEventListener('DOMContentLoaded', initSliders)
